<template>
  <div class="mt-60 global-search">
    <MainLayout>
      <div class="container">
        <h2 class="title search-title text-left">
          {{ title }}
        </h2>

        <el-tabs v-model="tabType">
          <el-tab-pane
            v-for="(tab, index) in tabs"
            :key="index"
            :label="tab.title"
            :name="tab.type"
            class="button"
          >
            <p class="highlight text-left total-result-count">
              <span
                :class="
                  $i18n.locale == 'fr-CA'
                    ? 'total-result-count-1'
                    : 'total-result-count-1-en'
                "
                >{{ getTotalResults("PRODUCT") }} {{ totalResultLabel }}</span
              >
              <span
                :class="
                  $i18n.locale == 'fr-CA'
                    ? 'total-result-count-2'
                    : 'total-result-count-2-en'
                "
                >{{ getTotalResults("PROVIDER") }} {{ totalResultLabel }}
              </span>
              <span class="total-result-count-3"
                >{{ getTotalResults("BLOG") }} {{ totalResultLabel }}</span
              >
            </p>

            <div v-if="isProductTab(tab.type) && changeProductView">
              <ProductCardsList
                :products="getSearchProduct"
                :span="cardSpanSize"
                :is-filter-visible="false"
                :tax-code="actionBar.tax"
              />
            </div>

            <div v-if="isProviderTab(tab.type)">
              <ProviderCardsList
                :providers="providers && providers.results"
                :span="cardSpanSize"
                :is-filter-visible="false"
                :is-shorting="false"
              />
            </div>

            <div v-if="isBlogTab(tab.type)">
              <BlogCardsList
                :blogs="blogs && blogs.results"
                :span="cardSpanSize"
                :is-filter-visible="false"
              />
            </div>
          </el-tab-pane>
        </el-tabs>

        <div v-if="products.total > 0">
          <el-row>
            <mq-layout :mq="$mq" class="container">
              <el-col :span="24">
                <div :class="isMobileLayout() ? 'text-center' : 'text-right'">
                  <Pagination
                    :current-page="getCurrentPage()"
                    :total-number-of-page="getTotalNumberOfPage()"
                    @click-previous="clickPrevious"
                    @click-next="clickNext"
                  />
                </div>
              </el-col>
            </mq-layout>
          </el-row>
        </div>
      </div>
    </MainLayout>
  </div>
</template>

<script>
import { mapState, mapActions } from "vuex";

import { ceil } from "@/utils/lodash";
import layoutUtils from "@/utils/layout-utils";
import { getDefaultAvailability } from "@/utils/market-utils";

import MainLayout from "@/components/layout/main-layout.vue";
import Pagination from "@/components/pagination.vue";

import ProductCardsList from "@/views/product/components/product-cards-list.vue";
import ProviderCardsList from "@/views/provider/components/provider-cards-list.vue";
import BlogCardsList from "@/views/blog/components/blog-cards-list.vue";

const tabTypeCodes = {
  PRODUCT: "PRODUCT",
  PROVIDER: "PROVIDER",
  BLOG: "BLOG",
};

export default {
  components: {
    MainLayout,
    Pagination,
    ProductCardsList,
    ProviderCardsList,
    BlogCardsList,
  },
  props: {
    text: {
      type: String,
      default: "",
    },
    activeTabType: {
      type: String,
      default: tabTypeCodes.PRODUCT,
    },
    activeCurrentPage: {
      type: Number,
      default: 1,
    },
  },
  data() {
    return {
      tabType: this.activeTabType,
      currentPage: {
        product: 1,
        provider: 1,
        blog: 1,
      },
      maxSize: 20,
      changeProductView: true,
    };
  },
  computed: {
    ...mapState("configuration", ["actionBar"]),
    ...mapState("product", ["products"]),
    ...mapState("provider", ["providers"]),
    ...mapState("blog", ["blogs"]),
    getSearchProduct() {
      if (!this.products || !this.products.results) return [];
      const priority = getDefaultAvailability(this.actionBar.country).map(c => c.toLowerCase());
      const byProductId = new Map();
      this.products.results.forEach((row, idx) => {
        const code = row.inventory && row.inventory.code;
        const rank = code ? priority.indexOf(code.toLowerCase()) : -1;
        if (rank === -1) return;
        const existing = byProductId.get(row.id);
        if (!existing || rank < existing.rank) {
          byProductId.set(row.id, { rank, row, idx: existing ? existing.idx : idx });
        }
      });
      return Array.from(byProductId.values())
        .sort((a, b) => a.idx - b.idx)
        .map(({ row }) => ({ ...row, erpProduct: row }));
    },
    totalResultLabel() {
      return this.$i18n.t("globalSearch.totalResult.label");
    },
    cardSpanSize() {
      switch (this.$mq) {
        case "lg":
          return 8;
        case "md":
        case "sm":
        case "xs":
          return 12;
        default:
          return 6;
      }
    },
    title() {
      return this.$i18n.t("globalSearch.title");
    },
    tabs() {
      return [
        {
          title: this.$i18n.t("globalSearch.tabs.product"),
          type: tabTypeCodes.PRODUCT,
        },
        {
          title: this.$i18n.t("globalSearch.tabs.provider"),
          type: tabTypeCodes.PROVIDER,
        },
        {
          title: this.$i18n.t("globalSearch.tabs.blogs"),
          type: tabTypeCodes.BLOG,
        },
      ];
    },
    changeLanguage() {
      return this.$i18n.locale;
    },
  },
  watch: {
    text() {
      this.resetCurrentPageValues();
      this.globalSearch();
    },
    tabType() {
      this.setActiveTabToQuery();
      this.setCurrentActiveTabPageToQuery();
    },
    currentPage: {
      handler() {
        this.setCurrentActiveTabPageToQuery();
      },
      deep: true,
    },
    changeLanguage() {
      this.globalSearch();
    },
    products(val) {
      if (!val || !val.results) return;
      this.changeProductView = false;
      this.$nextTick(() => {
        this.changeProductView = true;
      });
    },
  },
  mounted() {
    this.setCurrentPage();
    this.globalSearch();
    localStorage.setItem("viewType", "tuiles");
    this.$root.$on("chancountryCode", () => {
      this.$nextTick(() => {
        this.globalSearch();
      });
    });
  },
  methods: {
    ...mapActions("product", ["fetchProducts"]),
    ...mapActions("provider", ["fetchProviders"]),
    ...mapActions("blog", ["fetchBlogs"]),
    globalSearch() {
      this.getProducts();
      this.getProviders();
      this.getBlogs();
    },
    getProducts() {
      this.fetchProducts({
        filters: {
          text: this.text,
          page: this.currentPage.product,
          size: this.maxSize,
          availability: getDefaultAvailability(this.actionBar.country),
        },
      });
    },
    getProviders() {
      this.fetchProviders({
        type: this.actionBar.country,
        filters: {
          text: this.text,
          page: this.currentPage.provider,
          size: this.maxSize,
          // Scope to Boires providers only (QC → 1, ON → 2), excluding
          // Planvin/Négoce sheets.
          portausCompanyId: this.actionBar.country === 'ON' ? 2 : 1,
        },
      });
    },
    getBlogs() {
      this.fetchBlogs({
        filters: {
          text: this.text,
          page: this.currentPage.blog,
          size: this.maxSize,
        },
      });
    },
    isProductTab(type) {
      return tabTypeCodes.PRODUCT === type;
    },
    isProviderTab(type) {
      return tabTypeCodes.PROVIDER === type;
    },
    isBlogTab(type) {
      return tabTypeCodes.BLOG === type;
    },
    isMobileLayout() {
      return layoutUtils.isMobileLayout(this.$mq);
    },
    getTotalResults(type) {
      switch (type) {
        case tabTypeCodes.PRODUCT:
          return this.getSearchProduct.length;
        case tabTypeCodes.PROVIDER:
          return this.providers.total;
        case tabTypeCodes.BLOG:
          return this.blogs.total;
        default:
          return 0;
      }
    },
    getCurrentPage() {
      if (this.getTotalResults(this.tabType) === 0) {
        return 0;
      }

      switch (this.tabType) {
        case tabTypeCodes.PRODUCT:
          return this.currentPage.product;
        case tabTypeCodes.PROVIDER:
          return this.currentPage.provider;
        case tabTypeCodes.BLOG:
          return this.currentPage.blog;
        default:
          return 0;
      }
    },
    setCurrentPage() {
      switch (this.tabType) {
        case tabTypeCodes.PRODUCT:
          this.currentPage.product = this.activeCurrentPage;
          break;
        case tabTypeCodes.PROVIDER:
          this.currentPage.provider = this.activeCurrentPage;
          break;
        case tabTypeCodes.BLOG:
          this.currentPage.blog = this.activeCurrentPage;
          break;
        default:
      }
    },
    resetCurrentPageValues() {
      this.currentPage.product = 1;
      this.currentPage.provider = 1;
      this.currentPage.blog = 1;
    },
    getTotalNumberOfPage() {
      if (this.maxSize <= 0) {
        throw new Error(
          "@/views/global-search.vue : maxSize cannot be smaller or equal to zero"
        );
      }

      let total = 0;

      switch (this.tabType) {
        case tabTypeCodes.PRODUCT:
          total = this.getSearchProduct.length;
          break;
        case tabTypeCodes.PROVIDER:
          total = this.providers.total;
          break;
        case tabTypeCodes.BLOG:
          total = this.blogs.total;
          break;
        default:
          break;
      }
      return ceil(total / this.maxSize);
    },
    clickNext() {
      const nextPage = (currentPageType, getFn) => {
        if (this.currentPage[currentPageType] < this.getTotalNumberOfPage()) {
          this.currentPage[currentPageType] += 1;
          getFn();
        }
      };

      switch (this.tabType) {
        case tabTypeCodes.PRODUCT:
          nextPage("product", this.getProducts);
          break;
        case tabTypeCodes.PROVIDER:
          nextPage("provider", this.getProviders);
          break;
        case tabTypeCodes.BLOG:
          nextPage("blog", this.getBlogs);
          break;
        default:
          break;
      }
    },
    clickPrevious() {
      const previousPage = (currentPageType, getFn) => {
        if (this.currentPage[currentPageType] > 1) {
          this.currentPage[currentPageType] -= 1;
          getFn();
        }
      };

      switch (this.tabType) {
        case tabTypeCodes.PRODUCT:
          previousPage("product", this.getProducts);
          break;
        case tabTypeCodes.PROVIDER:
          previousPage("provider", this.getProviders);
          break;
        case tabTypeCodes.BLOG:
          previousPage("blog", this.getBlogs);
          break;
        default:
          break;
      }
    },
    setActiveTabToQuery() {
      this.$router
        .push({
          query: {
            ...this.$route.query,
            activeTabType: this.tabType,
          },
        })
        .catch(() => {});
    },
    setCurrentActiveTabPageToQuery() {
      this.$router
        .push({
          query: {
            ...this.$route.query,
            activeCurrentPage: this.getCurrentPage(),
          },
        })
        .catch(() => {});
    },
  },
};
</script>
<style scoped lang="scss">
.total-result-count {
  .total-result-count-1 {
    width: 110px;
    display: inline-block;
  }
  .total-result-count-1-en {
    width: 114px;
    display: inline-block;
  }
  .total-result-count-2 {
    width: 140px;
    display: inline-block;
  }
  .total-result-count-2-en {
    width: 118px;
    display: inline-block;
  }
  .total-result-count-3 {
    width: 107px;
    display: inline-block;
  }
}
@media only screen and (max-width: 1024px) {
  .total-result-count {
    .total-result-count-1 {
      width: 101px;
    }
    .total-result-count-1-en {
      width: 106px;
    }
    .total-result-count-2 {
      width: 130px;
    }
    .total-result-count-2-en {
      width: 112px;
    }
  }
}
.search-title {
  font-family: 'Futura LT Book', 'Futura', sans-serif;
}
</style>